import { TableFilter } from "@/types/TableFilter";
import { TableView } from "@/types/TableView";
import {
  createTableFilterEvents,
  createTableFilterGuests,
  deleteTableFilterEvents,
  deleteTableFilterGuests,
  getTableFilterEventsById,
  getTableFilterGuestsById,
  getTableFiltersEvents,
  getTableFiltersGuests,
  searchTableFiltersEvents,
  searchTableFiltersGuests,
  updateTableFilterEvents,
  updateTableFilterGuests,
} from "@/lib/api/clients/TableFilterClient";
import {
  createTableViewEvents,
  createTableViewGuests,
  deleteTableViewEvents,
  deleteTableViewGuests,
  getTableViewEventsById,
  getTableViewGuestsById,
  getTableViewsEvent,
  getTableViewsGuests,
  searchTableViewsEvents,
  searchTableViewsGuests,
  updateTableViewEvents,
  updateTableViewGuests,
  VIEWS_FIELDS,
} from "@/lib/api/clients/TableViewClient";
import {
  defaultView,
  defaultFilter,
  FIELDS_EVENT,
  FIELDS_GUEST,
  FIELDS_REGISTRANT,
  FIELDS_COMPANION,
  FIELDS_EMAIL_SENT,
  FIELDS_ACTION_LOG,
  FIELDS_SPEAKER,
  FIELDS_SPONSOR,
} from "@/lib/utils/default-returner";
import { handleErrorServer, ServerError } from "@/lib/api/errors/ErrorHandler";
import { getAdditionalFieldKeysAndTypes } from "@/lib/api/clients/EventClient";
import { getEvent } from "@/lib/api/handlers/EventHandling";
import { Event, BuiltInFieldConditions } from "@/types/Event";
import { Field, isComputedField } from "@/types/Field";
import {
  parseEventAdditionalFields,
  isNoValueOperator,
  flattenArrayFilterValues,
} from "@/lib/utils/parser";
import { getAdditionalFields } from "@/lib/api/clients/AdditionalFieldClient";
import { hasNonEmptyConditions } from "@/components/ConditionBuilder/condition-utils";
import { TableViewType } from "@/types/enums/TableViewType";

export type ViewFilterType =
  | "event"
  | "guest"
  | "registrant"
  | "companion"
  | "emailSent"
  | "checkIn"
  | "eventLimits"
  | "actionLog"
  // CRM contact pool — v1 has NO saved contact views/filters, so the
  // persistence paths are never invoked with it; the member exists so shared
  // UI (Table header/sorting, filter inputs) type-checks on the CRM surface.
  | "contact"
  // Wave A — speaker/sponsor hub DataTables. Event-scoped, guest-family
  // saved views/filters with type=SPEAKER/SPONSOR.
  | "speaker"
  | "sponsor";

// Common field selectors. VIEWS_FIELDS is imported from TableViewClient —
// it used to be duplicated here, and the copies drifting apart would silently
// strip fields from either the list fetch or the by-id (active view) fetch.
const FILTERS_FIELDS = "tableFilterId,name,filter,conditions,description,updatedAt";

// Helper function to map type to TableViewType. Exported for the lockstep
// type-routing test — the `default: GUEST` arm is a documented silent-fallback
// trap (an unmapped new type would quietly persist its views/filters under
// the GUEST family), so every non-guest type gets an EXPLICIT case.
export function getTableViewTypeFromViewFilterType(
  type: ViewFilterType
): TableViewType {
  switch (type) {
    case "event":
      return TableViewType.EVENT;
    case "emailSent":
      return TableViewType.EMAIL_SENT;
    case "checkIn":
      return TableViewType.CHECK_IN;
    case "actionLog":
      return TableViewType.ACTION_LOG;
    case "speaker":
      return TableViewType.SPEAKER;
    case "sponsor":
      return TableViewType.SPONSOR;
    default:
      return TableViewType.GUEST;
  }
}

// Helper to check if a type requires eventId
function requiresEventId(
  type: ViewFilterType
): boolean {
  return (
    type === "guest" ||
    type === "registrant" ||
    type === "companion" ||
    type === "emailSent" ||
    type === "checkIn" ||
    type === "eventLimits" ||
    type === "actionLog" ||
    type === "speaker" ||
    type === "sponsor"
  );
}

export async function initializeViewFilterEvents(
  initialViewId: string | null,
  initialFilterId: string | null
): Promise<[TableView, TableFilter] | ServerError> {
  try {
    let initialView = defaultView("event");
    let initialFilter = defaultFilter();


    // Changed return type
    const initialViewFunction = initialViewId
      ? getTableViewEventsById(initialViewId)
      : Promise.resolve(defaultView("event"));
    const initialFilterFunction = initialFilterId
      ? getTableFilterEventsById(initialFilterId)
      : Promise.resolve(defaultFilter());

    const results = await Promise.allSettled([
      initialViewFunction,
      initialFilterFunction,
    ]);

    // Extract results with proper handling of fulfilled/rejected states
    if (results[0].status === "fulfilled") initialView = results[0].value;
    if (results[1].status === "fulfilled") initialFilter = results[1].value;

    return [initialView, initialFilter];
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function initializeViewFilterGuests(
  initialViewId: string | null,
  initialFilterId: string | null,
  eventId: string,
  type: ViewFilterType = "guest"
): Promise<[TableView, TableFilter, Field[]] | ServerError> {
  try {
    let initialView = defaultView(type);
    let initialFilter = defaultFilter();
    let allFields: Field[] | ServerError = [];

    const viewType = getTableViewTypeFromViewFilterType(type);

    // Changed return type
    const initialViewFunction = initialViewId
      ? getTableViewGuestsById(initialViewId, eventId, viewType)
      : Promise.resolve(defaultView(type));
    const initialFilterFunction = initialFilterId
      ? getTableFilterGuestsById(
          initialFilterId,
          eventId,
          viewType
        )
      : Promise.resolve(defaultFilter());

    const results = await Promise.allSettled([
      initialViewFunction,
      initialFilterFunction,
      getFields(type, eventId),
    ]);

    // Extract results with proper handling of fulfilled/rejected states
    if (results[0].status === "fulfilled") initialView = results[0].value;
    if (results[1].status === "fulfilled") initialFilter = results[1].value;
    if (results[2].status === "fulfilled") allFields = results[2].value;

    if ("isServerError" in allFields && allFields.isServerError === true) {
      // If it's a ServerError, return it immediately
      return allFields;
    }

    return [initialView, initialFilter, allFields as Field[]];
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function fetchFiltersList(
  excludedIds: string[],
  type: ViewFilterType,
  eventId?: string,
  page?: number,
  size?: number
): Promise<TableFilter[] | ServerError> {
  try {
    if (!excludedIds) throw new Error("validationError");
    if (requiresEventId(type) && !eventId) throw new Error("validationError");
    // The filters list is ZERO-based (`TableFilterService` rejects page < 0);
    // the views list below is ONE-based. The `&&` chain that stood here could
    // not fire for any number (PB-RTI-1).
    if (page !== undefined && (page < 0 || page > 1000)) throw new Error("validationError");
    if (size && size < 0 && size > 100) throw new Error("validationError");
    excludedIds = excludedIds.filter((id) => id !== "root");

    if (type === "event") {
      return await getTableFiltersEvents(
        excludedIds,
        FILTERS_FIELDS,
        page,
        size
      );
    } else if (requiresEventId(type)) {
      const viewType = getTableViewTypeFromViewFilterType(type);
      return await getTableFiltersGuests(
        eventId!,
        excludedIds,
        FILTERS_FIELDS,
        page,
        size,
        viewType
      );
    }

    // just for linter no chance to go here
    return [];
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function fetchViewsList(
  excludedIds: string[],
  type: ViewFilterType,
  eventId?: string,
  page?: number,
  size?: number
): Promise<TableView[] | ServerError> {
  try {

    if (requiresEventId(type) && !eventId) throw new Error("validationError");
    if (!excludedIds) throw new Error("validationError");
    // The views list is ONE-based (`TableViewService` rejects page < 1 with a
    // 400 the callers swallowed as "no saved views"); the filters list above
    // is ZERO-based. The `&&` chain that stood here could not fire for any
    // number (PB-RTI-1).
    if (page !== undefined && (page < 1 || page > 1000)) throw new Error("validationError");
    if (size && size < 0 && size > 100) throw new Error("validationError");
    excludedIds = excludedIds.filter((id) => id !== "root");

    if (type === "event") {
      return await getTableViewsEvent(
        excludedIds,
        VIEWS_FIELDS,
        page,
        size
      );
    } else if (requiresEventId(type)) {
      const viewType = getTableViewTypeFromViewFilterType(type);
      return await getTableViewsGuests(
        eventId!,
        excludedIds,
        VIEWS_FIELDS,
        page,
        size,
        viewType
      );
    }

    // just for linter no chance to go here
    return [];
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function deleteView(
  viewId: string,
  type: ViewFilterType,
  eventId?: string
): Promise<void | ServerError> {
  try {

    if (requiresEventId(type) && !eventId) throw new Error("validationError");

    if (type === "event") {
      return await deleteTableViewEvents(viewId);
    } else if (requiresEventId(type)) {
      return await deleteTableViewGuests(eventId!, viewId);
    }
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function deleteFilter(
  filterId: string,
  type: ViewFilterType,
  eventId?: string
): Promise<void | ServerError> {
  try {

    if (requiresEventId(type) && !eventId) throw new Error("validationError");

    if (type === "event") {
      return await deleteTableFilterEvents(filterId);
    } else if (requiresEventId(type)) {
      return await deleteTableFilterGuests(eventId!, filterId);
    }
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function updateView(
  view: TableView,
  type: ViewFilterType,
  eventId?: string
): Promise<void | ServerError> {
  try {

    const nameTrimmed = view.name.trim();
    const descriptionTrimmed = view.description?.trim();

    if (requiresEventId(type) && !eventId) throw new Error("validationError");
    if (view.fields?.length === 0) throw new Error("validationError");
    if (nameTrimmed?.length === 0) throw new Error("min1CharName");
    if (nameTrimmed?.length > 50) throw new Error("max50CharName");
    if ((descriptionTrimmed?.length ?? 0) > 250)
      throw new Error("max250CharDescription");
    if (nameTrimmed === "DEFAULT") throw new Error("nameTrimmedDefault");

    if (type === "event") {
      return await updateTableViewEvents(view);
    } else if (requiresEventId(type)) {
      return await updateTableViewGuests(eventId!, view);
    }
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function updateFilter(
  filter: TableFilter,
  type: ViewFilterType,
  eventId?: string
): Promise<void | ServerError> {
  try {

    const nameTrimmed = filter.name.trim();
    const descriptionTrimmed = filter.description?.trim();

    if (requiresEventId(type) && !eventId) throw new Error("validationError");
    // A conditions-backed filter may have an empty flat derivation (OR trees
    // are not flat-representable) — the nested tree is the query then.
    if (filter.filter?.length === 0 && !hasNonEmptyConditions(filter.conditions))
      throw new Error("uniqueFiltersLengthError");
    if (nameTrimmed?.length === 0) throw new Error("min1CharName");
    if (nameTrimmed?.length > 50) throw new Error("max50CharName");
    if ((descriptionTrimmed?.length ?? 0) > 250)
      throw new Error("max250CharDescription");
    if (nameTrimmed === "DEFAULT") throw new Error("nameTrimmedDefault");

    // Split multi-pick array values into scalar OR-rows — the backend
    // Filter.value is a single String (see flattenArrayFilterValues).
    filter.filter = flattenArrayFilterValues(filter.filter);

    if (type === "event") {
      return await updateTableFilterEvents(filter);
    } else if (requiresEventId(type)) {
      return await updateTableFilterGuests(eventId!, filter);
    }
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function createView(
  view: TableView,
  type: ViewFilterType,
  eventId?: string
): Promise<string | ServerError> {
  try {

    const nameTrimmed = view.name.trim();
    const descriptionTrimmed = view.description?.trim();

    if (requiresEventId(type) && !eventId) throw new Error("validationError");
    if (view.fields?.length === 0) throw new Error("min1View");
    if (nameTrimmed?.length === 0) throw new Error("min1CharName");
    if (nameTrimmed?.length > 50) throw new Error("max50CharName");
    if ((descriptionTrimmed?.length || 0) > 250)
      throw new Error("max250CharDescription");
    if (nameTrimmed === "DEFAULT") throw new Error("nameTrimmedDefault");
    view.tableViewType = getTableViewTypeFromViewFilterType(type);

    if (type === "event") {
      return await createTableViewEvents(view);
    } else if (requiresEventId(type)) {
      return await createTableViewGuests(eventId!, view);
    }

    // This is just for the linter - the function will never reach here
    throw new Error("validationError");
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function createFilter(
  filter: TableFilter,
  type: ViewFilterType,
  eventId?: string
): Promise<string | ServerError> {
  try {

    const nameTrimmed = filter.name.trim();
    const descriptionTrimmed = filter.description?.trim();

    // Split multi-pick array values into scalar OR-rows BEFORE clean/dedup —
    // the backend Filter.value is a single String (see flattenArrayFilterValues).
    const flatFilter = flattenArrayFilterValues(filter.filter);
    const cleanedFilter = flatFilter.filter(
      (f) => f.key && f.operator && (isNoValueOperator(f.operator) || (f.value !== null && f.value !== ""))
    );
    const uniqueFilters = cleanedFilter.filter(
      (filterItem, index, self) =>
        index ===
        self.findIndex(
          (t) =>
            t.key === filterItem.key &&
            t.operator === filterItem.operator &&
            t.value === filterItem.value
        )
    );

    if (uniqueFilters?.length === 0 && !hasNonEmptyConditions(filter.conditions))
      throw new Error("uniqueFiltersLengthError");
    if (requiresEventId(type) && !eventId) throw new Error("validationError");
    if (nameTrimmed?.length === 0) throw new Error("min1CharName");
    if (nameTrimmed?.length > 50) throw new Error("max50CharName");
    if ((descriptionTrimmed?.length || 0) > 250)
      throw new Error("max250CharDescription");
    if (nameTrimmed === "DEFAULT") throw new Error("nameTrimmedDefault");

    filter.filter = uniqueFilters;
    filter.tableViewType = getTableViewTypeFromViewFilterType(type);

    if (type === "event") {
      return await createTableFilterEvents(filter);
    } else if (requiresEventId(type)) {
      return await createTableFilterGuests(eventId!, filter);
    }

    // This is just for the linter - the function will never reach here
    throw new Error("validationError");
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function searchViews(
  searchTerm: string,
  type: ViewFilterType,
  eventId?: string
): Promise<TableView[] | ServerError> {
  try {
    if (requiresEventId(type) && !eventId) throw new Error("validationError");
    if (searchTerm.trim()?.length < 3) return [];


    if (type === "event") {
      return await searchTableViewsEvents(
        searchTerm,
        VIEWS_FIELDS,
      );
    } else if (requiresEventId(type)) {
      const viewType = getTableViewTypeFromViewFilterType(type);
      return await searchTableViewsGuests(
        eventId!,
        searchTerm,
        VIEWS_FIELDS,
        1,
        10,
        viewType
      );
    }

    // just for linter no chance to go here
    return [];
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function searchFilters(
  searchTerm: string,
  type: ViewFilterType,
  eventId?: string
): Promise<TableFilter[] | ServerError> {
  try {
    if (requiresEventId(type) && !eventId) throw new Error("validationError");
    if (searchTerm.trim()?.length < 3) return [];


    if (type === "event") {
      return await searchTableFiltersEvents(
        searchTerm,
        FILTERS_FIELDS,
      );
    } else if (requiresEventId(type)) {
      const viewType = getTableViewTypeFromViewFilterType(type);
      return await searchTableFiltersGuests(
        eventId!,
        searchTerm,
        FILTERS_FIELDS,
        0,
        10,
        viewType
      );
    }

    // just for linter no chance to go here
    return [];
  } catch (error) {
    return handleErrorServer(error);
  }
}

// Best-effort fetch of the event-level built-in field overlays — display
// colors AND conditional-display rules, both keyed by built-in field name.
// Failure (or a ServerError) returns empties so built-ins fall back to their
// defaults. One event read covers both side-maps.
async function getBuiltInFieldOverlays(eventId: string): Promise<{
  displayColors?: Record<string, Record<string, string>>;
  fieldConditions?: Record<string, BuiltInFieldConditions>;
  fieldRequired?: Record<string, boolean>;
}> {
  try {
    const ev = await getEvent(
      eventId,
      "fieldDisplayColors,fieldConditions,fieldRequired",
    );
    if (ev && typeof ev === "object" && !("isServerError" in ev)) {
      return {
        displayColors: (ev as Event).fieldDisplayColors,
        fieldConditions: (ev as Event).fieldConditions,
        fieldRequired: (ev as Event).fieldRequired,
      };
    }
  } catch {
    // ignore — defaults apply
  }
  return {};
}

export async function getFields(
  type: ViewFilterType,
  eventId?: string,
  /** Ride the admin's own auth instead of an active preview bearer (role editor
   *  needs the full field catalogue). Only honored for guest-like types. */
  bypassPreview = false,
): Promise<Field[] | ServerError> {
  try {
    let fields: Field[] = [];

    if (type === "event") {
      const response = await getAdditionalFieldKeysAndTypes();
      fields = parseEventAdditionalFields(response);
      fields.push(...FIELDS_EVENT);
    }
    // actionLog has a fixed schema (no per-event additional fields)
    if (type === "actionLog") {
      return [...FIELDS_ACTION_LOG];
    }
    // Handle all guest-like types
    if (requiresEventId(type) && eventId) {
      // Only guest/checkIn carry COLORABLE built-in enums (state/gender/
      // acceptanceState); skip the colors fetch for the others. Run it
      // concurrently with the additional-fields fetch (don't serialize).
      const isGuestLike = type === "guest" || type === "checkIn";
      // Entity-scoped definitions (Wave A): the speaker/sponsor tables load
      // ONLY their own target's custom fields. Guest-family types omit the
      // param entirely (request stays byte-identical to pre-Wave-A).
      const target =
        type === "speaker" ? "SPEAKER" : type === "sponsor" ? "SPONSOR" : undefined;
      const [fetchedFields, overlays] = await Promise.all([
        getAdditionalFields(
          eventId,
          // IT-04 — `sensitivity` rides this projection because the field
          // editor has to show the level it is about to overwrite. The PUT is a
          // partial update and a missing level leaves the stored one alone, so
          // nothing unsets itself; what breaks without this is the dialog,
          // which would open every marked field showing "Normal" and let an
          // organiser downgrade one while believing they changed nothing.
          // `formula` rides for the same reason `sensitivity` does: it is what
          // the field editor is about to overwrite, and it is what decides
          // whether the column may be edited at all. Without it every computed
          // field would open as an ordinary one and be offered as a write
          // target everywhere.
          "fieldId,name,translation,dataType,required,manualCheck,options,optionColors,optionCapacity,lowerBound,upperBound,currencyCode,defaultValue,visibilityCondition,editableCondition,optionConditions,sensitivity,formula",
          bypassPreview,
          target,
        ),
        isGuestLike
          ? getBuiltInFieldOverlays(eventId)
          : Promise.resolve(
              {} as Awaited<ReturnType<typeof getBuiltInFieldOverlays>>,
            ),
      ]);
      const displayColors = overlays.displayColors;
      const fieldConditions = overlays.fieldConditions;
      const fieldRequired = overlays.fieldRequired;
      fields = fetchedFields.map((field) => ({
        ...field,
        name: field.name,
        // `editable` is minted HERE, not sent by the backend — the definition
        // has no such property. A COMPUTED field is the one custom field
        // nobody may type into: its value is re-derived after every write, so
        // an accepted edit would vanish within the same request. Deriving it
        // at this single point makes every surface that already honours
        // `editable` (the bulk-edit target picker, the inline table cell)
        // correct without a clause of its own.
        editable: !isComputedField(field),
        additionalField: true,
      }));

      const builtIns =
        type === "guest" || type === "checkIn"
          ? FIELDS_GUEST
          : type === "registrant"
          ? FIELDS_REGISTRANT
          : type === "emailSent"
          ? FIELDS_EMAIL_SENT
          : type === "speaker"
          ? FIELDS_SPEAKER
          : type === "sponsor"
          ? FIELDS_SPONSOR
          : FIELDS_COMPANION;

      // Hydrate built-in enum colors from the event-level fieldDisplayColors
      // side-map so the guestlist + dashboard render the user's chosen colors,
      // and the conditional-display rules from fieldConditions so built-in
      // fields lock/hide exactly like custom ones. Spread-clone the shared
      // FIELDS_* constants before attaching anything so the constants are never
      // mutated.
      fields.push(
        ...builtIns.map((f) => {
          const colorOverlay = displayColors?.[f.name];
          const cond = fieldConditions?.[f.name];
          const req = fieldRequired?.[f.name];
          return {
            ...f,
            ...(colorOverlay
              ? { optionColors: { ...(f.optionColors ?? {}), ...colorOverlay } }
              : {}),
            ...(cond?.visibilityCondition
              ? { visibilityCondition: cond.visibilityCondition }
              : {}),
            ...(cond?.editableCondition
              ? { editableCondition: cond.editableCondition }
              : {}),
            ...(cond?.optionConditions
              ? { optionConditions: cond.optionConditions }
              : {}),
            // Per-event required override; falls back to the constant's value.
            ...(req !== undefined ? { required: req } : {}),
          };
        }),
      );
    }
    return fields;
  } catch (error) {
    return handleErrorServer(error);
  }
}
